import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSupabaseAuth } from '@/hooks/auth';
import { supabase } from '@/integrations/supabase/client';
import { useEffect } from 'react';

interface DashboardStats {
  totalCourses: number;
  completedCourses: number;
  inProgressCourses: number;
  notStartedCourses: number;
  overallProgress: number;
  certificateCount: number;
  learningStreak: number;
  loading: boolean;
}

/**
 * Optimized dashboard stats using batch RPC queries
 * Reduces 191 queries to just 3 parallel queries
 */
export const useRealDashboardStats = (): DashboardStats => {
  const { user, userRole } = useSupabaseAuth();
  const queryClient = useQueryClient();

  // Set up real-time subscription
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('dashboard-progress-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_module_progress',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['dashboard-stats', user.id] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);

  const { data, isLoading } = useQuery<Omit<DashboardStats, 'loading'>>({
    queryKey: ['dashboard-stats', user?.id],
    queryFn: async () => {
      if (!user) {
        return {
          totalCourses: 0,
          completedCourses: 0,
          inProgressCourses: 0,
          notStartedCourses: 0,
          overallProgress: 0,
          certificateCount: 0,
          learningStreak: 0,
        };
      }

      // 3 parallel batch queries instead of 191 individual queries
      const [progressResult, certificatesResult, activityResult] = await Promise.all([
        supabase.rpc('get_user_courses_with_progress', { p_user_id: user.id }),
        supabase.from('certificates').select('id').eq('user_id', user.id),
        supabase
          .from('module_activity_logs')
          .select('created_at')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(100),
      ]);

      const coursesWithProgress = progressResult.data || [];
      
      // Calculate stats from single batch result
      let completedCourses = 0;
      let inProgressCourses = 0;
      let notStartedCourses = 0;
      let totalProgress = 0;

      for (const course of coursesWithProgress) {
        const progress = course.overall_progress || 0;
        totalProgress += progress;
        
        if (progress === 100) {
          completedCourses++;
        } else if (progress > 0) {
          inProgressCourses++;
        } else {
          notStartedCourses++;
        }
      }

      const totalCourses = coursesWithProgress.length;
      const overallProgress = totalCourses > 0 
        ? Math.round(totalProgress / totalCourses) 
        : 0;

      // Calculate learning streak from batch result
      const learningStreak = calculateLearningStreak(activityResult.data || []);

      return {
        totalCourses,
        completedCourses,
        inProgressCourses,
        notStartedCourses,
        overallProgress,
        certificateCount: certificatesResult.data?.length || 0,
        learningStreak,
      };
    },
    enabled: !!user,
    staleTime: 2 * 60 * 1000, // 2 minutes - data stays fresh
    gcTime: 10 * 60 * 1000, // 10 minutes cache retention
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  return {
    totalCourses: data?.totalCourses ?? 0,
    completedCourses: data?.completedCourses ?? 0,
    inProgressCourses: data?.inProgressCourses ?? 0,
    notStartedCourses: data?.notStartedCourses ?? 0,
    overallProgress: data?.overallProgress ?? 0,
    certificateCount: data?.certificateCount ?? 0,
    learningStreak: data?.learningStreak ?? 0,
    loading: isLoading,
  };
};

function calculateLearningStreak(activityLogs: { created_at: string }[]): number {
  if (!activityLogs.length) return 0;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const uniqueDays = new Set<string>();
  activityLogs.forEach(log => {
    const logDate = new Date(log.created_at);
    logDate.setHours(0, 0, 0, 0);
    uniqueDays.add(logDate.toDateString());
  });

  const sortedDays = Array.from(uniqueDays)
    .map(day => new Date(day))
    .sort((a, b) => b.getTime() - a.getTime());

  let streak = 0;
  let currentDate = new Date(today);

  for (const day of sortedDays) {
    if (day.getTime() === currentDate.getTime()) {
      streak++;
      currentDate.setDate(currentDate.getDate() - 1);
    } else if (day.getTime() < currentDate.getTime()) {
      break;
    }
  }

  return streak;
}